/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\DetailsPrix.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT DÉTAILS PRIX

📋 DESCRIPTION:
Composant React d'affichage détaillé des calculs de prix d'un article.
Interface complète de décomposition des coûts avec visualisation graphique et support calepinage.

🎯 RESPONSABILITÉS:
- Affichage détaillé des surfaces calculées (dessus, fond, bandeau) avec géométrie précise
- Décomposition complète des longueurs de couture par type avec majorations
- Visualisation temps de main d'œuvre avec détails préparation et finition
- Affichage coûts matières avec informations calepinage et optimisation
- Décomposition accessoires (fermetures adaptatives, mousses, œillets, sangles)
- Calculs TVA adaptatifs selon statut entreprise (HT/TTC automatique)
- Interface toggle pour masquer/afficher détails avec sections collapsibles
- Support option grille avec calculs spécialisés

🔗 DÉPENDANCES:
- React: Hooks useState pour gestion état affichage
- Types: CalculPrix pour structure données prix détaillées
- Utilitaires: Formatage montants et temps localisés
- Services: Intégration calculs tarifs_service et calepinage

📡 INTERACTIONS:
- Parent: ConfigurateurArticle pour données prix calculées temps réel
- Backend: Réception structure détaillée calculs API avec calepinage
- Interface: Toggle affichage détails utilisateur avec persistance état
- Calculs: Affichage résultats tarifs_service avec 6 formes supportées
- Calepinage: Informations matières optimisées et économies réalisées

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Décembre 2024
👤 Responsable: Équipe développement

💡 NOTES:
- Formatage localisé montants en euros (fr-FR) avec précision 2 décimales
- Conversion automatique temps minutes → heures avec arrondi intelligent
- Affichage conditionnel selon options activées (grille, fermetures, mousses)
- Support calepinage avec informations matières détaillées et économies
- Gestion TVA entreprise (HT/TTC automatique) selon configuration
- Interface responsive avec sections collapsibles et animations
- Support 6 formes d'articles avec calculs spécialisés par forme
- Intégration fermetures adaptatives avec longueurs calculées
- Affichage volumes mousses avec classification épaisseur
*/
import React from 'react';
import { CalculPrix } from './types';
import { TempsMainOeuvre } from '../../../services/types';
import TempsMainOeuvreDetails from '../../../components/Calepinage/TempsMainOeuvreDetails';

interface DetailsPrixProps {
  prixCalcule: CalculPrix;
  showDetailsPrix: boolean;
  setShowDetailsPrix: (show: boolean) => void;
  quantite: number; // Ajouter cette ligne
}

/**
 * Fonction pour formater les dimensions d'un élément
 * CORRIGÉE : Gère maintenant les formes rondes et utilise les bonnes propriétés
 */
const formatElementDimensions = (element: any, type: string, prixCalcule: CalculPrix): string => {
  if (!element) {
    return 'N/A';
  }
  
  // Utiliser directement les dimensions du backend si disponibles
  if (element.longueur && element.largeur) {
    return `${element.longueur.toFixed(1)} × ${element.largeur.toFixed(1)} cm`;
  }
  
  // Pour les éléments circulaires - vérifier diametre_reel d'abord
  if (element.diametre_reel) {
    return `Ø${element.diametre_reel.toFixed(1)} cm`;
  }
  
  // Pour les éléments circulaires - diametre standard
  if (element.diametre) {
    return `Ø${element.diametre.toFixed(1)} cm`;
  }
  
  // Pour les formes rondes et cylindriques, utiliser les dimensions de base du prixCalcule
  if (prixCalcule.article_forme && (prixCalcule.article_forme.includes('rond') || prixCalcule.article_forme.includes('cylindre'))) {
    const dims = prixCalcule.details.dimensions;
    const talon = dims.talon || 1;
    
    // Gestion spécifique pour les cylindres
    if (prixCalcule.article_forme.includes('cylindre')) {
      if (type === 'joue') {
        // Pour les joues de cylindre : Ø(D + 2T)
        if (dims.diametre) {
          const diametre_final = dims.diametre + 2 * talon;
          return `Ø${diametre_final.toFixed(1)} cm`;
        }
      } else if (type === 'cylindre') {
        // Pour le corps du cylindre : (π×(D + 2T)) × (L + 2T)
        if (dims.diametre && dims.longueur) {
          const corps_longueur = Math.PI * (dims.diametre + 2 * talon);
          const corps_largeur = dims.longueur + 2 * talon;
          return `${corps_longueur.toFixed(1)} × ${corps_largeur.toFixed(1)} cm`;
        }
      }
    } else {
      // Gestion pour les formes rondes (non cylindriques)
      if (type === 'dessus' || type === 'fond') {
        // Pour dessus/fond rond : (D + 2T)
        if (dims.diametre) {
          const diametre_final = dims.diametre + 2 * talon;
          return `Ø${diametre_final.toFixed(1)} cm`;
        }
      } else if (type === 'bandeau') {
        // Pour bandeau rond : (π×(D + 2T)) × (H + 2T)
        if (dims.diametre && dims.hauteur) {
          const bandeau_longueur = Math.PI * (dims.diametre + 2 * talon);
          const bandeau_hauteur = dims.hauteur + 2 * talon;
          return `${bandeau_longueur.toFixed(1)} × ${bandeau_hauteur.toFixed(1)} cm`;
        }
      }
    }
  }
  
  // Si l'élément est vide, retourner N/A
  return 'N/A';
};

const getDetailDims = (prixCalcule: CalculPrix, keys: string[]): any | undefined => {
  const details = prixCalcule?.details?.dimensions?.details || {};
  for (const key of keys) {
    const candidate = (details as any)[key];
    if (candidate) return candidate;
  }
  return undefined;
};

/**
 * Fonction pour formater un temps en heures décimales vers le format "1h30mn"
 * @param heures Temps en heures décimales (ex: 1.5)
 * @returns Temps formaté (ex: "1h30mn")
 */
const formatHeures = (heures: number): string => {
  const h = Math.floor(heures);
  const mn = Math.round((heures - h) * 60);
  return `${h}h${mn.toString().padStart(2, '0')}mn`;
};

// Fonction pour formater les accessoires
const formatAccessoires = (accessoires: CalculPrix['details']['accessoires'] | undefined): string => {
  if (!accessoires) return 'Aucun';
  
  const items = [];
  
  if (accessoires.fermeture) {
    items.push(`Fermeture ${accessoires.fermeture}`);
  }
  
  if (accessoires.mousse) {
    items.push(`Mousse ${accessoires.mousse} (${accessoires.volumeMousse?.toFixed(3)} m³)`);
  }
  
  return items.length > 0 ? items.join(', ') : 'Aucun';
};

// Récupère les pièces d'un type précis depuis le calepinage backend
const getPiecesByType = (prixCalcule: CalculPrix | any, type: string): any[] => {
  const calepinage = prixCalcule?.details?.calepinage;
  if (!calepinage?.matieres) return [];

  return calepinage.matieres.flatMap((matiere: any) =>
    (matiere.pieces || []).filter((piece: any) => piece?.type === type)
  );
};

// Formate les dimensions d'une pièce (rectangulaire ou circulaire)
const formatPieceDimensions = (piece: any): string => {
  if (!piece) return 'N/A';

  const round = (v: any) => (typeof v === 'number' ? v.toFixed(1) : undefined);

  const width = round(piece.width ?? piece.longueur ?? piece.length ?? piece.largeur);
  const height = round(piece.height ?? piece.largeur ?? piece.hauteur);

  if (width && height) {
    return `${width} × ${height} cm`;
  }

  const diametre = round(piece.diametre_reel ?? piece.diametre);
  if (diametre) {
    return `Ø${diametre} cm`;
  }

  return 'N/A';
};


const DetailsPrix: React.FC<DetailsPrixProps> = (props) => {
  // État local pour contrôler l'affichage détaillé - DOIT être en premier
  const [showFullDetails, setShowFullDetails] = React.useState(false);
  
  
  
  // ✅ CORRECTION : Convertir les valeurs individuelles selon le statut TVA
  const soumisATVA = props.prixCalcule?.soumisATVA ?? true;
  const tauxTVA = props.prixCalcule?.tauxTVA ?? 20;
  const facteurConversion = soumisATVA ? (1 + tauxTVA / 100) : 1;
  
  // Convertir les valeurs TTC en HT si l'entreprise est soumise à TVA
  const coutFermeture = ((props.prixCalcule?.details?.accessoires as any)?.coutFermeture ?? 0) / facteurConversion;
  const coutMousse = ((props.prixCalcule?.details?.accessoires as any)?.coutMousse ?? 0) / facteurConversion;
  const coutOeillets = ((props.prixCalcule?.details?.accessoires as any)?.coutOeillets ?? 0) / facteurConversion;
  const coutRenforts = ((props.prixCalcule?.details?.accessoires as any)?.coutRenforts ?? 0) / facteurConversion;
  const coutSangles = ((props.prixCalcule?.details?.accessoires as any)?.coutSangles ?? 0) / facteurConversion;
  const coutAssemblage = ((props.prixCalcule?.details?.accessoires as any)?.coutAssemblage ?? 0) / facteurConversion;
  const quantite = props.quantite ?? 1;
  
  
  
  // ✅ UTILISER LA VALEUR DU BACKEND au lieu de recalculer
  const coutAccessoires = props.prixCalcule.details.couts.accessoires;

  // Données grille issues du backend (calepinage)
  const grillePieces = getPiecesByType(props.prixCalcule, 'grille_mesh');
  const bandeFondPieces = getPiecesByType(props.prixCalcule, 'bande_fond');
  const optionGrilleActive = Boolean(
    (props.prixCalcule as any)?.options?.grille ||
    grillePieces.length > 0 ||
    bandeFondPieces.length > 0
  );
  
  
  if (!props.showDetailsPrix || !props.prixCalcule) {
    return null;
  }

  // Vue condensée
  if (!showFullDetails) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Résumé du prix</h2>
          <div className="flex space-x-2">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              onClick={() => setShowFullDetails(true)}
            >
              Voir les détails complets
            </button>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700"
              onClick={() => props.setShowDetailsPrix(false)}
            >
              <span className="sr-only">Fermer</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-600 mb-4">
          L'utilisateur reste responsable de la vérification des données saisies et du montant affiché avant intégration au devis.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Résumé des coûts */}
          <div>
            <h3 className="text-md font-medium text-gray-800 mb-3">Répartition des coûts</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Main d'œuvre:</span>
                <span className="text-sm font-medium">{props.prixCalcule.details.couts.main_oeuvre.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Matières:</span>
                <span className="text-sm font-medium">
                  {props.prixCalcule.soumisATVA 
                    ? props.prixCalcule.details.couts.matieres.toFixed(2) 
                    : (props.prixCalcule.details.couts.matieres * 1.2).toFixed(2)} €
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Accessoires:</span>
                <span className="text-sm font-medium">{coutAccessoires.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-sm font-medium text-gray-600">
                  {props.prixCalcule.soumisATVA ? "Total HT:" : "Total net:"}
                </span>
                <span className="text-sm font-medium">{(props.prixCalcule.prixUnitaire ?? (props.prixCalcule.details.couts.main_oeuvre + props.prixCalcule.details.couts.matieres + coutAccessoires)).toFixed(2)} â‚¬</span>
              </div>
            </div>
          </div>

          {/* Informations principales */}
          <div>
            <h3 className="text-md font-medium text-gray-800 mb-3">Informations principales</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Surface totale:</span>
                <span className="text-sm font-medium">{props.prixCalcule.details.surfaces.total.toFixed(2)} m²</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Coutures totales:</span>
                <span className="text-sm font-medium">{props.prixCalcule.details.coutures.total.toFixed(2)} m</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Temps total:</span>
                <span className="text-sm font-medium">{formatHeures(props.prixCalcule.details.temps.total_heures)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Accessoires:</span>
                <span className="text-sm font-medium">{formatAccessoires(props.prixCalcule.details.accessoires)}</span>
              </div>
            </div>
          </div>

          {/* Prix final */}
          <div>
            <h3 className="text-md font-medium text-gray-800 mb-3">Prix final</h3>
            <div className="space-y-2">
              {/* Ajouter la quantité */}
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Quantité:</span>
                <span className="text-sm font-medium">{props.quantite}</span>
              </div>
              
              {/* Prix unitaire */}
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Prix unitaire {props.prixCalcule.soumisATVA ? 'HT:' : 'net:'}</span>
                <span className="text-sm font-medium">
                  {(props.prixCalcule.prixUnitaire ?? ((props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total) / props.quantite)).toFixed(2)} €
                </span>
              </div>
              
              {/* Prix total */}
              {props.prixCalcule.soumisATVA ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total HT:</span>
                    <span className="text-lg font-bold text-primary-600">{(props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">TVA ({props.prixCalcule.tauxTVA}%):</span>
                    <span className="text-sm font-medium">{((props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total) * props.prixCalcule.tauxTVA / 100).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-sm font-medium text-gray-600">Total TTC:</span>
                    <span className="text-xl font-bold text-primary-600">
                      {(props.prixCalcule.prixTTC ?? ((props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total) * (1 + props.prixCalcule.tauxTVA / 100))).toFixed(2)} €
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium text-gray-600">Total net:</span>
                  <span className="text-xl font-bold text-primary-600">{(props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total).toFixed(2)} €</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vue détaillée complète (code existant)
  return (
    <div className="bg-white shadow rounded-lg p-6 mt-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Détails du prix</h2>
          <p className="text-xs text-gray-500">Sections ci-dessous : valeurs par unité (hors calepinage).</p>
          <p className="text-xs text-gray-600 mt-1">
            L'utilisateur reste responsable de la vérification des données saisies et du montant affiché avant intégration au devis.
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            onClick={() => setShowFullDetails(false)}
          >
            Masquer les détails
          </button>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={() => props.setShowDetailsPrix(false)}
          >
            <span className="sr-only">Fermer</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Dimensions détaillées */}
        <div>
          <h3 className="text-md font-medium text-gray-800 mb-2">Dimensions avec talon</h3>
          <div className="space-y-2">
            {props.prixCalcule.details?.dimensions?.details && (
              <>
                {/* Rectangulaire avec bandeau */}
                {props.prixCalcule.article_forme && props.prixCalcule.article_forme.includes('rectangulaire_bandeau') && !props.prixCalcule.article_forme.includes('demi_bandeau') && (
                  <>
                    {props.prixCalcule.details.dimensions.details?.dessus && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Dessus:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['dessus']), 'dessus', props.prixCalcule)}</span>
                      </div>
                    )}
                    {props.prixCalcule.details.dimensions.details?.fond && props.prixCalcule.details.surfaces.fond > 0 && !(props.prixCalcule as any).options?.grille && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Dessous:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['fond']), 'fond', props.prixCalcule)}</span>
                      </div>
                    )}
                    {props.prixCalcule.details.dimensions.details?.bandeau && props.prixCalcule.details.surfaces.bandeau > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Bandeau:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['bandeau']), 'bandeau', props.prixCalcule)}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Rectangulaire demi-bandeau (sans biseau) */}
                {props.prixCalcule.article_forme && props.prixCalcule.article_forme.includes('rectangulaire_demi_bandeau') && !props.prixCalcule.article_forme.includes('biseau') && (
                  <>
                    
                    {props.prixCalcule.details.dimensions.details?.dessus && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Plateau central:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['dessus', 'plateau_central']), 'dessus', props.prixCalcule)}</span>
                      </div>
                    )}
                    {props.prixCalcule.details.dimensions.details?.bandeau && props.prixCalcule.details.surfaces.bandeau > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Demi-bandeau:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['bandeau']), 'bandeau', props.prixCalcule)}</span>
                      </div>
                    )}
                    {props.prixCalcule.details.dimensions.details?.fond && props.prixCalcule.details.surfaces.fond > 0 && !(props.prixCalcule as any).options?.grille && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Fond:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['fond']), 'fond', props.prixCalcule)}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Rectangulaire avec joues (standard) */}
                {props.prixCalcule.article_forme && props.prixCalcule.article_forme.includes('rectangulaire_joues') && !props.prixCalcule.article_forme.includes('biseau') && (
                  <>
                    {/* Joues (utiliser les données du dessus pour les joues) */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Joue 1:</span>
                      <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['joue1', 'dessus']), 'joue', props.prixCalcule)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Joue 2:</span>
                      <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['joue2', 'dessus']), 'joue', props.prixCalcule)}</span>
                    </div>
                    
                    {/* Bandeau central */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Bandeau central:</span>
                      <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['bandeau', 'bandeau_central', 'bandeau-plateau', 'bandeau_plateau']), 'bandeau_central', props.prixCalcule)}</span>
                    </div>
                    
                    {/* Dessous (fond) si présent */}
                    {props.prixCalcule.details.surfaces.fond > 0 && !(props.prixCalcule as any).options?.grille && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Dessous:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(getDetailDims(props.prixCalcule, ['fond']), 'fond', props.prixCalcule)}</span>
                      </div>
                    )}
                    
                    {/* Grille mesh + Bandes fond (option grille) */}
                    {optionGrilleActive && (
                      <>
                        {grillePieces.length > 0 ? (
                          grillePieces.map((piece, idx) => (
                            <div className="flex justify-between" key={`grille-piece-${idx}`}>
                              <span className="text-sm text-gray-600">
                                Grille mesh{grillePieces.length > 1 ? ` ${idx + 1}` : ''}:
                              </span>
                              <span className="text-sm font-medium">{formatPieceDimensions(piece)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Grille mesh:</span>
                            <span className="text-sm font-medium">
                              {(() => {
                                const dims = props.prixCalcule.details.dimensions;
                                const talon = dims.talon || 1;
                                const dimensionPrincipale = Math.max(dims.longueur || 0, dims.largeur || 0);
                                const dimensionSecondaire = Math.min(dims.longueur || 0, dims.largeur || 0);
                                
                                // Calcul largeur grille (même logique que le backend)
                                const largeurFermeture = 2.0;
                                const largeurBandeMin = 3.0;
                                const espaceDisponible = dimensionSecondaire - largeurFermeture;
                                const grilleLargeurIdeal = espaceDisponible * 0.8;
                                const espaceBandes = espaceDisponible - grilleLargeurIdeal;
                                const largeurBande = Math.max(largeurBandeMin, espaceBandes / 3);
                                const grilleLargeur = dimensionSecondaire - (3 * largeurBande) - largeurFermeture;
                                
                                const grilleLongueur = dimensionPrincipale + 2 * talon;
                                const grilleLargeurAvecTalon = grilleLargeur + 2 * talon;
                                
                                return `${grilleLongueur.toFixed(1)} × ${grilleLargeurAvecTalon.toFixed(1)} cm`;
                              })()}
                            </span>
                          </div>
                        )}

                        {bandeFondPieces.length > 0 ? (
                          bandeFondPieces.map((piece, idx) => (
                            <div className="flex justify-between" key={`bande-fond-piece-${idx}`}>
                              <span className="text-sm text-gray-600">
                                Bande fond {bandeFondPieces.length > 1 ? `${idx + 1}` : ''}:
                              </span>
                              <span className="text-sm font-medium">{formatPieceDimensions(piece)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Bandes fond (3×):</span>
                            <span className="text-sm font-medium">
                              {(() => {
                                const dims = props.prixCalcule.details.dimensions;
                                const talon = dims.talon || 1;
                                const dimensionPrincipale = Math.max(dims.longueur || 0, dims.largeur || 0);
                                const dimensionSecondaire = Math.min(dims.longueur || 0, dims.largeur || 0);
                                
                                // Calcul largeur bandes (même logique que le backend)
                                const largeurFermeture = 2.0;
                                const largeurBandeMin = 3.0;
                                const espaceDisponible = dimensionSecondaire - largeurFermeture;
                                const grilleLargeurIdeal = espaceDisponible * 0.8;
                                const espaceBandes = espaceDisponible - grilleLargeurIdeal;
                                const largeurBande = Math.max(largeurBandeMin, espaceBandes / 3);
                                
                                const bandeLongueur = dimensionPrincipale + 2 * talon;
                                const bandeLargeur = largeurBande + 2 * talon;
                                
                                return `${bandeLongueur.toFixed(1)} × ${bandeLargeur.toFixed(1)} cm`;
                              })()}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Rectangle joues biseau */}
                {props.prixCalcule.article_forme && props.prixCalcule.article_forme === 'rectangle_joues_biseau' && (
                  <>
                    {/* Joues trapézoïdales */}
                    {(() => {
                      const detailsDims = props.prixCalcule.details.dimensions.details || {};
                      const joue1 = (detailsDims as any).joue1 || (detailsDims as any).joue || (detailsDims as any).joues;
                      const joue2 = (detailsDims as any).joue2 || joue1;

                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Joue trapézoïdale 1:</span>
                            <span className="text-sm font-medium">
                              {formatElementDimensions(joue1, 'joue', props.prixCalcule)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Joue trapézoïdale 2:</span>
                            <span className="text-sm font-medium">
                              {formatElementDimensions(joue2, 'joue', props.prixCalcule)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                    
                    {/* Bandeau central */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Bandeau central:</span>
                      <span className="text-sm font-medium">
                        {(() => {
                          const detailsDims = props.prixCalcule.details.dimensions.details || {};
                          const bandeau = (detailsDims as any).bandeau_central || (detailsDims as any).bandeau;
                          return formatElementDimensions(bandeau, 'bandeau_central', props.prixCalcule);
                        })()}
                      </span>
                    </div>
                    
                    {/* Fond si présent */}
                    {props.prixCalcule.details.surfaces.fond > 0 && !(props.prixCalcule as any).options?.grille && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Fond:</span>
                        <span className="text-sm font-medium">
                          {(() => {
                            const dims = props.prixCalcule.details.dimensions;
                            const talon = dims.talon || 1;
                            const longueur = dims.longueur || 0;
                            const largeur_dessous = dims.largeur_dessous || 0;
                            if (longueur && largeur_dessous) {
                              const longueur_finale = longueur + 2 * talon;
                              const largeur_finale = largeur_dessous + 2 * talon;
                              return `${longueur_finale.toFixed(1)} × ${largeur_finale.toFixed(1)} cm`;
                            }
                            return 'N/A';
                          })()}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Rectangle demi-bandeau biseau */}
                {props.prixCalcule.article_forme && props.prixCalcule.article_forme === 'rectangle_demi_bandeau_biseau' && (
                  <>
                    {/* Plateau central */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Plateau central:</span>
                      <span className="text-sm font-medium">
                        {formatElementDimensions(getDetailDims(props.prixCalcule, ['plateau_central', 'dessus']), 'plateau_central', props.prixCalcule)}
                      </span>
                    </div>
                    
                    {/* Bandeau */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Bandeau:</span>
                      <span className="text-sm font-medium">
                        {formatElementDimensions(getDetailDims(props.prixCalcule, ['bandeau']), 'bandeau', props.prixCalcule)}
                      </span>
                    </div>
                    
                    {/* Fond si présent */}
                    {props.prixCalcule.details.surfaces.fond > 0 && !(props.prixCalcule as any).options?.grille && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Fond:</span>
                        <span className="text-sm font-medium">
                          {formatElementDimensions(getDetailDims(props.prixCalcule, ['fond']), 'fond', props.prixCalcule)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Rond avec bandeau */}
                {props.prixCalcule.article_forme && (props.prixCalcule.article_forme.includes('rond_bandeau') || props.prixCalcule.article_forme.includes('rond_avec_bandeau')) && (
                  <>
                    {props.prixCalcule.details.dimensions.details?.dessus && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Dessus:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(props.prixCalcule.details.dimensions.details?.dessus, 'dessus', props.prixCalcule)}</span>
                      </div>
                    )}
                    {props.prixCalcule.details.dimensions.details?.fond && props.prixCalcule.details.surfaces.fond > 0 && !(props.prixCalcule as any).options?.grille && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Dessous:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(props.prixCalcule.details.dimensions.details?.fond, 'fond', props.prixCalcule)}</span>
                      </div>
                    )}
                    {props.prixCalcule.details.dimensions.details?.bandeau && props.prixCalcule.details.surfaces.bandeau > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Bandeau:</span>
                        <span className="text-sm font-medium">{formatElementDimensions(props.prixCalcule.details.dimensions.details?.bandeau, 'bandeau', props.prixCalcule)}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Rectangle Boîte */}
                {props.prixCalcule.article_forme && props.prixCalcule.article_forme === 'rectangle_boite' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Plateau développé:</span>
                      <span className="text-sm font-medium">
                        {(() => {
                          const dims = props.prixCalcule.details.dimensions;
                          const talon = dims.talon || 1;
                          if (dims.longueur && dims.largeur && dims.hauteur) {
                            const longueur = dims.longueur + 2 * dims.hauteur + 2 * talon;
                            const largeur = dims.largeur + 2 * dims.hauteur + 2 * talon;
                            return `${longueur.toFixed(1)} × ${largeur.toFixed(1)} cm`;
                          }
                          return 'N/A';
                        })()}
                      </span>
                    </div>
                    {props.prixCalcule.details.surfaces.fond > 0 && !(props.prixCalcule as any).options?.grille && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Fond:</span>
                        <span className="text-sm font-medium">
                          {(() => {
                            const dims = props.prixCalcule.details.dimensions;
                            const talon = dims.talon || 1;
                            if (dims.longueur && dims.largeur) {
                              const longueur = dims.longueur + 2 * talon;
                              const largeur = dims.largeur + 2 * talon;
                              return `${longueur.toFixed(1)} × ${largeur.toFixed(1)} cm`;
                            }
                            return 'N/A';
                          })()}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Cylindre avec joues */}
                {props.prixCalcule.article_forme && props.prixCalcule.article_forme.includes('cylindre_joues') && (
                  <>
                    {/* Joue 1 */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Joue 1:</span>
                      <span className="text-sm font-medium">
                        {formatElementDimensions(
                          // Utiliser un objet vide pour forcer l'utilisation de la logique de secours dans formatElementDimensions
                          {}, 
                          'joue',
                          props.prixCalcule
                        )}
                      </span>
                    </div>
                    
                    {/* Joue 2 */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Joue 2:</span>
                      <span className="text-sm font-medium">
                        {formatElementDimensions(
                          // Utiliser un objet vide pour forcer l'utilisation de la logique de secours dans formatElementDimensions
                          {}, 
                          'joue',
                          props.prixCalcule
                        )}
                      </span>
                    </div>
                    
                    {/* Corps du cylindre */}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Corps du cylindre:</span>
                      <span className="text-sm font-medium">
                        {formatElementDimensions(
                          // Utiliser un objet vide pour forcer l'utilisation de la logique de secours dans formatElementDimensions
                          {}, 
                          'cylindre',
                          props.prixCalcule
                        )}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium text-gray-600">Talon:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.dimensions.talon} cm</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Surfaces */}
        <div>
          <h3 className="text-md font-medium text-gray-800 mb-2">Surfaces</h3>
          <div className="space-y-2">
            {props.prixCalcule.article_forme === 'rectangle_joues_biseau' ? (
              // Rectangle joues biseau : utiliser les valeurs du backend avec les bons libellés
              <>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bandeau central:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.surfaces.bandeau.toFixed(2)} m²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Joues trapézoïdales (2×):</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.surfaces.dessus.toFixed(2)} m²</span>
                </div>
              </>
            ) : (
              // Autres formes : affichage standard
              <>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Dessus:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.surfaces.dessus.toFixed(2)} m²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bandeau/Joues:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.surfaces.bandeau.toFixed(2)} m²</span>
                </div>
              </>
            )}
            {!(props.prixCalcule as any).options?.grille && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Fond:</span>
                <span className="text-sm font-medium">{props.prixCalcule.details.surfaces.fond.toFixed(2)} m²</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-medium text-gray-600">Total:</span>
              <span className="text-sm font-medium">
                {(() => {
                  let total = props.prixCalcule.details.surfaces.total;
                  // Si l'option grille est activée, soustraire la surface du fond du total
                  if ((props.prixCalcule as any).options?.grille) {
                    total = total - props.prixCalcule.details.surfaces.fond;
                  }
                  return total.toFixed(2);
                })()} m²
              </span>
            </div>
          </div>
        </div>

        {/* Coutures */}
        <div>
          <h3 className="text-md font-medium text-gray-800 mb-2">Coutures</h3>
          <div className="space-y-2">
            {/* Affichage différent selon la forme */}
            {props.prixCalcule.article_forme === 'cylindre_joues' ? (
              // Cylindre avec joues
              <>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Joue avant ↔ Corps:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.coutures.dessus.toFixed(2)} m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Joue arrière ↔ Corps:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.coutures.fond.toFixed(2)} m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Jonction corps:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.coutures.jonction.toFixed(2)} m</span>
                </div>
              </>
            ) : props.prixCalcule.article_forme === 'rectangle_simple' ? (
              // Bâche (rectangle simple)
              <>
                {props.prixCalcule.details.coutures.dessus > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Périmètre ourlet:</span>
                    <span className="text-sm font-medium">{props.prixCalcule.details.coutures.dessus.toFixed(2)} m</span>
                  </div>
                )}
                {props.prixCalcule.details.coutures.jonction > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Assemblage lés:</span>
                    <span className="text-sm font-medium">{props.prixCalcule.details.coutures.jonction.toFixed(2)} m</span>
                  </div>
                )}
              </>
            ) : (
              // Autres formes (rectangulaires et rondes)
              <>
                {/* Dessus - avec libellés sémantiques selon la forme */}
                {props.prixCalcule.article_forme !== 'rectangulaire_demi_bandeau' && props.prixCalcule.details.coutures.dessus > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      {props.prixCalcule.article_forme === 'rectangulaire_bandeau' 
                        ? "Dessus ↔ Bandeau:"
                        : props.prixCalcule.article_forme === 'rectangle_boite'
                        ? "[Plateau développé]"
                        : props.prixCalcule.article_forme === 'rectangle_joues_biseau'
                        ? "Joues ↔ Bandeau:"
                        : props.prixCalcule.article_forme === 'rectangle_demi_bandeau_biseau'
                        ? "Plateau biseau ↔ Bandeau:"
                        : props.prixCalcule.article_forme?.includes('rond')
                        ? "Dessus ↔ Bandeau:"
                        : "Dessus:"}
                    </span>
                    <span className="text-sm font-medium">{props.prixCalcule.details.coutures.dessus.toFixed(2)} m</span>
                  </div>
                )}
                
                {/* Fond - avec libellés sémantiques selon la forme */}
                {props.prixCalcule.details.coutures.fond > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      {props.prixCalcule.article_forme === 'rectangulaire_bandeau'
                        ? "Fond ↔ Bandeau:"
                        : props.prixCalcule.article_forme === 'rectangulaire_joues'
                        ? "Fond ↔ Joues ↔ Bandeau central:"
                        : props.prixCalcule.article_forme === 'rectangulaire_demi_bandeau'
                        ? "Fond ↔ Bandeau ↔ Plateau:"
                        : props.prixCalcule.article_forme === 'rectangle_boite'
                        ? "Fond ↔ Plateau:"
                        : props.prixCalcule.article_forme === 'rectangle_joues_biseau'
                        ? "Fond ↔ Bandeau ↔ Joues:"
                        : props.prixCalcule.article_forme === 'rectangle_demi_bandeau_biseau'
                        ? "Fond ↔ Bandeau ↔ Plateau biseau:"
                        : props.prixCalcule.article_forme?.includes('rond')
                        ? "Fond ↔ Bandeau:"
                        : "Fond:"}
                    </span>
                    <span className="text-sm font-medium">{props.prixCalcule.details.coutures.fond.toFixed(2)} m</span>
                  </div>
                )}
                
                {/* Jonction - avec libellés sémantiques selon la forme */}
                {props.prixCalcule.details.coutures.jonction > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      {props.prixCalcule.article_forme === 'rectangulaire_bandeau'
                        ? "Jonction bandeau:"
                        : props.prixCalcule.article_forme === 'rectangulaire_joues'
                        ? "Joues ↔ Bandeau central:"
                        : props.prixCalcule.article_forme === 'rectangulaire_demi_bandeau'
                        ? "Plateau ↔ Bandeau:"
                        : props.prixCalcule.article_forme === 'rectangle_boite'
                        ? "Angles plateau:"
                        : props.prixCalcule.article_forme?.includes('rond')
                        ? "Jonction bandeau:"
                        : "Jonction:"}
                    </span>
                    <span className="text-sm font-medium">{props.prixCalcule.details.coutures.jonction.toFixed(2)} m</span>
                  </div>
                )}
              </>
            )}
            
            {/* Ligne de détail pour la grille (si présente) - avec libellés sémantiques */}
            {(props.prixCalcule.details.coutures as any).grille && (props.prixCalcule.details.coutures as any).grille > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {props.prixCalcule.article_forme === 'rectangulaire_bandeau'
                    ? "Grille + internes ↔ Bandeau:"
                    : props.prixCalcule.article_forme === 'rectangulaire_joues'
                    ? "Grille + internes ↔ Joues ↔ Bandeau central:"
                    : props.prixCalcule.article_forme === 'rectangulaire_demi_bandeau'
                    ? "Grille + internes ↔ Bandeau ↔ Plateau:"
                    : props.prixCalcule.article_forme === 'rectangle_boite'
                    ? "Grille + internes ↔ Plateau:"
                    : props.prixCalcule.article_forme === 'rectangle_joues_biseau'
                    ? "Grille + internes ↔ Bandeau ↔ Joues:"
                    : props.prixCalcule.article_forme === 'rectangle_demi_bandeau_biseau'
                    ? "Grille + internes ↔ Bandeau ↔ Plateau biseau:"
                    : props.prixCalcule.article_forme?.includes('rond')
                    ? "Grille ↔ Bandeau:"
                    : "Grille:"}
                </span>
                <span className="text-sm font-medium">{((props.prixCalcule.details.coutures as any).grille).toFixed(2)} m</span>
              </div>
            )}
            
            {/* Ligne de détail pour la couture fermeture (zip) */}
            {(props.prixCalcule.details.coutures as any).fermeture && (props.prixCalcule.details.coutures as any).fermeture > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Couture fermeture (zip):</span>
                <span className="text-sm font-medium">{((props.prixCalcule.details.coutures as any).fermeture).toFixed(2)} m</span>
              </div>
            )}
            
            {/* Coefficients de majoration */}
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Majoration pour surpiqûre/Passepoil:</span>
              <span className="text-sm font-medium">×{(props.prixCalcule.details as any).coefficientSurpiqure || "1.0"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Majoration pour forme complexe:</span>
              <span className="text-sm font-medium">×{(props.prixCalcule.details as any).coefficientFormeComplexe || "1.0"}</span>
            </div>
            {/* Coefficient de majoration total */}
            {'coefficientMajoration' in props.prixCalcule.details && (props.prixCalcule.details as any).coefficientMajoration > 1 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Coefficient total:</span>
                <span className="text-sm font-medium">×{(props.prixCalcule.details as any).coefficientMajoration}</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-medium text-gray-600">Total (avec majorations):</span>
              <span className="text-sm font-medium">{props.prixCalcule.details.coutures.total.toFixed(2)} m</span>
            </div>
          </div>
        </div>

        {/* Temps détaillé */}
        <div>
          <h3 className="text-md font-medium text-gray-800 mb-2">Temps de confection</h3>
          <div className="space-y-2">
            {/* Temps de couture */}
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 font-medium">Temps de couture:</span>
              <span className="text-sm font-medium">{props.prixCalcule.details.temps.couture} min</span>
            </div>
            
            {/* Temps de préparation avec détails */}
            <div className="flex flex-col">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 font-medium">Temps de préparation:</span>
                <span className="text-sm font-medium">
                  {(() => {
                    const preparationBackend = props.prixCalcule.details.temps.preparation;
                    const preparationCalculee = props.prixCalcule.details.temps.total_heures * 60 - props.prixCalcule.details.temps.couture;
                    
                    return preparationBackend?.toFixed(0) || preparationCalculee.toFixed(0);
                  })()} min
                </span>
              </div>
              <div className="ml-4 text-xs text-gray-500">
                {/* Affichage conditionnel : seuls les postes > 0 sont montrés */}
                {(() => {
                  const surfacesTotal = props.prixCalcule.details.surfaces?.total ?? 0;
                  const tempsDetaille = (props.prixCalcule.details.tempsDetaille || {}) as Partial<TempsMainOeuvre>;
                  const lignes = [
                    {
                      key: 'tempsBase',
                      label: 'Temps de base:',
                      value: tempsDetaille.tempsBase ?? 0,
                    },
                    {
                      key: 'dimensions',
                      label: `Supplément dimensions (${surfacesTotal ? surfacesTotal.toFixed(2) : '0.00'} m²):`,
                      value: surfacesTotal ? Math.max(0, Math.floor(surfacesTotal - 1)) : 0,
                    },
                    {
                      key: 'poseFermeture',
                      label: 'Pose de fermeture:',
                      value: tempsDetaille.poseFermeture ?? 0,
                    },
                    {
                      key: 'supplementMousse',
                      label: `Supplément mousse ${props.prixCalcule.details.accessoires.mousse || 'aucune'}:`,
                      value: tempsDetaille.supplementMousse ?? 0,
                    },
                    {
                      key: 'autres',
                      label: 'Préparation et finition:',
                      value: tempsDetaille.autres ?? 0,
                    },
                  ].filter((ligne) => ligne.value > 0);

                  return lignes.map((ligne) => (
                    <div className="flex justify-between" key={ligne.key}>
                      <span>{ligne.label}</span>
                      <span>{ligne.value.toFixed(1)} min</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
            
            {/* Temps total */}
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-medium text-gray-600">Total:</span>
              <span className="text-sm font-medium">{formatHeures(props.prixCalcule.details.temps.total_heures)}</span>
            </div>
          </div>
        </div>

        {/* Coûts */}
        <div>
          <h3 className="text-md font-medium text-gray-800 mb-2">Coûts</h3>
          <div className="space-y-2">
            {/* Main d'œuvre avec détails */}
            <div className="flex flex-col">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 font-medium">Main d'œuvre:</span>
                <span className="text-sm font-medium">{props.prixCalcule.details.couts.main_oeuvre.toFixed(2)} €</span>
              </div>
              <div className="ml-4 text-xs text-gray-500">
                <div className="flex justify-between">
                  <span>Temps total:</span>
                  <span>{formatHeures(props.prixCalcule.details.temps.total_heures)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Taux horaire:</span>
                  <span>{(props.prixCalcule.details.couts.main_oeuvre / props.prixCalcule.details.temps.total_heures).toFixed(2)} €/h</span>
                </div>
              </div>
            </div>
            
            {/* Matières avec détails */}
            <div className="flex flex-col">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 font-medium">Matières:</span>
                <span className="text-sm font-medium">
                  {props.prixCalcule.soumisATVA 
                    ? props.prixCalcule.details.couts.matieres.toFixed(2) 
                    : (props.prixCalcule.details.couts.matieres * 1.2).toFixed(2)} €
                </span>
              </div>
              {/* Section des détails des matières supprimée à la demande de l'utilisateur */}
            </div>
            
            {/* Accessoires avec détails */}
            <div className="flex flex-col">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 font-medium">Accessoires:</span>
                <span className="text-sm font-medium">{coutAccessoires.toFixed(2)} €</span>
              </div>
              <div className="ml-4 text-xs text-gray-500">
                {/* Accessoires communs à tous les types d'articles */}
                {props.prixCalcule.details.accessoires.fermeture && (
                  <div className="flex justify-between">
                    <span>Fermeture {props.prixCalcule.details.accessoires.fermeture}:</span>
                    <span>{(coutFermeture * props.quantite).toFixed(2)} €</span>
                  </div>
                )}
                {props.prixCalcule.details.accessoires.mousse && (
                  <div className="flex justify-between">
                    <span>
                      Mousse {props.prixCalcule.details.accessoires.mousse}:
                      {props.prixCalcule.details.dimensions && (
                        <span className="text-blue-600 ml-1">
                          {(() => {
                            const dims = props.prixCalcule.details.dimensions;
                            // Pour les formes cylindriques, afficher diamètre et longueur
                            if (props.prixCalcule.article_forme?.includes('cylindre')) {
                              return `Ø${dims.diametre}×${dims.longueur} cm`;
                            }
                            // Pour les formes rondes, afficher diamètre et hauteur
                            else if (props.prixCalcule.article_forme?.includes('rond')) {
                              return `Ø${dims.diametre}×${dims.hauteur} cm`;
                            }
                            // Pour les formes rectangulaires, afficher L×l×h
                            else {
                              return `${dims.longueur}×${dims.largeur}×${dims.hauteur} cm`;
                            }
                          })()}
                          ({props.prixCalcule.details.accessoires.volumeMousse?.toFixed(3)} m³)
                        </span>
                      )}
                    </span>
                    <span>{(coutMousse * props.quantite).toFixed(2)} €</span>
                  </div>
                )}
                
                {/* Accessoires spécifiques aux bâches */}
                {props.prixCalcule.article_forme === 'rectangle_simple' && (
                  <>
                    {/* Œillets */}
                    {coutOeillets > 0 && (
                      <div className="flex justify-between">
                        <span>Œillets ({(props.prixCalcule.details.accessoires as any)?.nombreOeillets || 0} pièces):</span>
                        <span>{(coutOeillets * props.quantite).toFixed(2)} €</span>
                      </div>
                    )}
                    
                    {/* Renforts aux angles */}
                    {coutRenforts > 0 && (
                      <div className="flex justify-between">
                        <span>Renforts aux angles ({(props.prixCalcule.details.accessoires as any)?.nombreRenforts || 4} pièces):</span>
                        <span>{(coutRenforts * props.quantite).toFixed(2)} €</span>
                      </div>
                    )}
                    
                    {/* Sangles */}
                    {coutSangles > 0 && (
                      <div className="flex justify-between">
                        <span>Sangles:</span>
                        <span>{(coutSangles * props.quantite).toFixed(2)} €</span>
                      </div>
                    )}
                    
                    {/* Assemblage - Note: maintenant uniquement dans le temps de main d'œuvre */}
                    {props.prixCalcule.details.coutures.jonction > 0 && (
                      <div className="flex justify-between">
                        <span>Assemblage (temps uniquement):</span>
                        <span>Inclus dans main d'œuvre</span>
                      </div>
                    )}
                  </>
                )}
                
                {/* Accessoires spécifiques aux coussins */}
                {props.prixCalcule.article_forme !== 'rectangle_simple' && (
                  <>
                    {/* Passepoil */}
                    {(props.prixCalcule as any).options && (props.prixCalcule as any).options.passepoil && (
                      <div className="flex justify-between">
                        <span>Passepoil ({props.prixCalcule.details.coutures.total.toFixed(2)} m):</span>
                        <span>{((props.prixCalcule as any).options.coutPassepoil || 
                          (props.prixCalcule.details.coutures.total * 1.50)).toFixed(2)} €</span>
                      </div>
                    )}
                    
                    
                    {/* Capitons */}
                    {(props.prixCalcule as any).options && (props.prixCalcule as any).options.capitons && (
                      <div className="flex justify-between">
                        <span>Capitons ({(props.prixCalcule as any).options.nombreCapitons || 4} pièces):</span>
                        <span>{((props.prixCalcule as any).options.coutCapitons || 
                          ((props.prixCalcule as any).options.nombreCapitons || 4) * 2.50).toFixed(2)} €</span>
                      </div>
                    )}
                  </>
                )}
                
                {/* Autres accessoires */}
                {(props.prixCalcule.details.accessoires as any).autres && Object.entries((props.prixCalcule.details.accessoires as any).autres).map(([key, value]: [string, any]) => (
                  <div key={key} className="flex justify-between">
                    <span>{key}:</span>
                    <span>{value.toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Total */}
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-medium text-gray-600">
                {props.prixCalcule.soumisATVA ? "Total HT:" : "Total net:"}
              </span>
              <span className="text-sm font-medium">{(props.prixCalcule.prixUnitaire ?? (props.prixCalcule.details.couts.main_oeuvre + props.prixCalcule.details.couts.matieres + coutAccessoires)).toFixed(2)} â‚¬</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section d'assemblage UNIQUEMENT pour rectangle_simple (bâches) quand largeur > laize */}
      {/* Les autres formes ont des coutures de jonction NORMALES (bandeau, joues, etc.) qui ne sont PAS des assemblages */}

      {/* Section spécifique pour les bâches */}
      {props.prixCalcule.article_forme === 'rectangle_simple' && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h3 className="text-md font-medium text-gray-800 mb-2">Informations spécifiques pour bâche</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations sur les dimensions totales avec ourlet */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Dimensions avec ourlet</h4>
              <div className="space-y-2">
                {props.prixCalcule.details.dimensions && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Longueur + Ourlet:</span>
                      <span className="text-sm font-medium">
                        {(props.prixCalcule.details.dimensions.longueur || 0) + 2 * (props.prixCalcule.details.dimensions.talon || 0)} cm
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Largeur + Ourlet:</span>
                      <span className="text-sm font-medium">
                        {(props.prixCalcule.details.dimensions.largeur || 0) + 2 * (props.prixCalcule.details.dimensions.talon || 0)} cm
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Dimension totale:</span>
                      <span className="text-sm font-medium">
                        {(props.prixCalcule.details.dimensions.longueur || 0) + 2 * (props.prixCalcule.details.dimensions.talon || 0)} × {(props.prixCalcule.details.dimensions.largeur || 0) + 2 * (props.prixCalcule.details.dimensions.talon || 0)} cm
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Périmètre:</span>
                      <span className="text-sm font-medium">
                        {(2 * (props.prixCalcule.details.dimensions.longueur || 0) + 
                          2 * (props.prixCalcule.details.dimensions.largeur || 0)).toFixed(1)} cm
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Informations sur les œillets */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Œillets</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Espacement standard:</span>
                  <span className="text-sm font-medium">30 cm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Nombre d'œillets estimé:</span>
                  <span className="text-sm font-medium">
                    {Math.ceil((2 * (props.prixCalcule.details.dimensions.longueur || 0) + 
                      2 * (props.prixCalcule.details.dimensions.largeur || 0)) / 30)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Périmètre couture ourlet:</span>
                  <span className="text-sm font-medium">
                    {(2 * (props.prixCalcule.details.dimensions.longueur || 0) + 
                      2 * (props.prixCalcule.details.dimensions.largeur || 0)).toFixed(1)} cm
                  </span>
                </div>
              </div>
            </div>
            
            {/* Informations sur l'assemblage */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Assemblage</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Largeur:</span>
                  <span className="text-sm font-medium">{props.prixCalcule.details.dimensions.largeur || 0} cm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Laize de la matière:</span>
                  <span className="text-sm font-medium">
                    {(() => {
                      // ✅ UTILISER la vraie laize envoyée par le backend
                      if (props.prixCalcule.details.calepinage && 
                          props.prixCalcule.details.calepinage.matieres && 
                          props.prixCalcule.details.calepinage.matieres.length > 0) {
                        const matiereCalepinage = props.prixCalcule.details.calepinage.matieres[0] as any;
                        
                        // Le backend envoie maintenant la laize dans les données
                        const laize = matiereCalepinage.laize || 150;
                        return `${laize} cm`;
                      }
                      return '150 cm';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Assemblage nécessaire:</span>
                  <span className="text-sm font-medium">
                    {props.prixCalcule.details.coutures.jonction > 0 ? (
                      <span className="text-amber-600 font-medium">Oui</span>
                    ) : (
                      <span className="text-green-600 font-medium">Non</span>
                    )}
                  </span>
                </div>
                {props.prixCalcule.details.coutures.jonction > 0 ? (
                  <div className="text-xs text-amber-600 mt-1">
                    Un assemblage de plusieurs pièces est nécessaire. Longueur de jonction: {props.prixCalcule.details.coutures.jonction.toFixed(2)} m.
                  </div>
                ) : (
                  <div className="text-xs text-green-600 mt-1">
                    Pas d'assemblage nécessaire. La pièce peut être découpée dans la laize disponible.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section pour les informations de calepinage */}
      {props.prixCalcule.details.calepinage && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h3 className="text-md font-medium text-gray-800 mb-2">Informations sur le calepinage</h3>
          <p className="text-xs text-gray-500 mb-3">
            La matière (ML) est calculée pour le lot complet (quantité actuelle). Elle peut ne pas être linéaire avec la quantité.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Utilisation du calepinage</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Calepinage utilisé:</span>
                  <span className="text-sm font-medium">
                    {props.prixCalcule.details.calepinage.utilisationCalepinage ? 'Oui' : 'Non'}
                  </span>
                </div>
                {props.prixCalcule.details.calepinage.utilisationCalepinage && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Majoration Standard:</span>
                      <span className="text-sm font-medium">
                        {props.prixCalcule.details.calepinage.majorationStandard ? (
                          <span className="text-amber-600">Oui (20%)</span>
                        ) : (
                          <span className="text-green-600">Non (Premium)</span>
                        )}
                      </span>
                    </div>
                    {props.prixCalcule.details.calepinage.majorationStandard && (
                      <div className="text-xs text-amber-600 mt-1">
                        Vous avez bénéficié d'un calepinage automatisé pour cet article, mais n'ayant pas accès à la visualisation en abonnement Standard, 
                        un coefficient de perte pour calepinage manuel de 20% a été appliqué sur le coût des matières.
                        Passez à l'abonnement Premium pour bénéficier du calepinage optimisé sans coefficient de perte et une visualisation et impression du plan de calepinage.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Calepinage par matière</h4>
              <div className="space-y-2">
                {props.prixCalcule.details.calepinage.matieres && props.prixCalcule.details.calepinage.matieres.length > 0 ? (
                  <>
                {props.prixCalcule.details.calepinage.matieres.map((matiere: any, index: number) => {
                  // Utiliser le nom de la matière depuis les données du backend si disponible
                  // Le backend peut envoyer matiere.nom ou on doit fallback sur l'ID
                  const nomMatiere = matiere.nom || matiere.reference || `Matière ${matiere.id}`;

                  // ✅ PATCH : Calculer les faces depuis pieces[] si faces[] est vide/absent
                  const facesAffichees = (() => {
                    // Fonction de normalisation des types de faces
                    const normaliserFace = (face: string): string => {
                      if (face === 'joue1' || face === 'joue2') return 'joues';
                      if (face === 'bande_fond') return 'bandes fond';
                      if (face === 'grille_mesh' || face === 'fond_mesh') return 'grille';
                      return face;
                    };

                    // Si matiere.faces existe et n'est pas vide, l'utiliser
                    if (matiere.faces && matiere.faces.length > 0) {
                      return Array.from(new Set(matiere.faces.map(normaliserFace))).join(', ');
                    }

                    // Sinon, fallback : extraire depuis matiere.pieces[]
                    if (matiere.pieces && matiere.pieces.length > 0) {
                      const typesPieces = matiere.pieces.map((p: any) => p.type).filter(Boolean);
                      return Array.from(new Set(typesPieces.map(normaliserFace))).join(', ');
                    }

                    return '';
                  })();

                  return (
                    <div key={index} className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        {nomMatiere} ({matiere.couleur}):
                        {facesAffichees && (
                          <span className="text-xs text-gray-500 ml-1">
                            {facesAffichees}
                          </span>
                        )}
                      </span>
                      <span className="text-sm font-medium">
                        {matiere.enStock ? (
                          <span className="text-green-600">En stock</span>
                        ) : (
                          <span className="text-amber-600">
                            {/* Libellé "Calepinage" supprimé à la demande de l'utilisateur */}
                          </span>
                        )}
                        {matiere.longueurMetres !== undefined && (
                          <span className="text-gray-600"> ({matiere.longueurMetres.toFixed(2)} ML)</span>
                        )}
                      </span>
                    </div>
                  );
                })}
                  </>
                ) : (
                  // Pas de données de calepinage disponibles
                  <div className="text-sm text-gray-500 italic">
                    Informations de calepinage non disponibles. Recalculez l'article pour les mettre à jour.
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  ML = Mètre Linéaire (longueur de matière nécessaire)
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200">
        {/* Affichage du prix selon le statut TVA */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium text-gray-900">Quantité:</span>
            <span className="text-xl font-bold text-gray-900">{props.quantite}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium text-gray-900">Prix unitaire {props.prixCalcule.soumisATVA ? 'HT:' : 'net:'}</span>
            <span className="text-xl font-bold text-primary-600">
              {(props.prixCalcule.prixUnitaire ?? ((props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total) / props.quantite)).toFixed(2)} €
            </span>
          </div>
          
          {props.prixCalcule.soumisATVA ? (
            <>
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-gray-900">Total HT:</span>
                <span className="text-xl font-bold text-primary-600">{(props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total).toFixed(2)} €</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-gray-900">Total TTC:</span>
                <span className="text-xl font-bold text-primary-600">
                  {(props.prixCalcule.prixTTC ?? ((props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total) * (1 + props.prixCalcule.tauxTVA / 100))).toFixed(2)} €
                </span>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-gray-900">Total net:</span>
              <span className="text-xl font-bold text-primary-600">{(props.prixCalcule.prixTotalArticle ?? props.prixCalcule.details.couts.total).toFixed(2)} €</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailsPrix;







